import { format } from "date-fns";
import { MDXClient } from "next-mdx-remote-client";
import { ArticleJsonLd, NextSeo } from "next-seo";

import BlogHero from "@/components/blog/BlogHero";
import BlogLayout from "@/components/blog/BlogLayout";
import BlogBreadcrumbs from "@/components/blog/Breadcrumbs";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import authors from "@/content/resources/authors";
import { getAllResourceSlugs, getResourcePostBySlug } from "@/lib/resources/loader";
import { mdxComponents } from "@/lib/resources/mdx-components";
import { extractTocFromSource } from "@/lib/resources/plugins/remark-extract-toc";
import { serializeResourceMdx } from "@/lib/resources/serialize";
import { runAllValidations } from "@/lib/resources/validation";

import type { GetStaticPaths, GetStaticProps } from "next";
import type { SerializeResult } from "next-mdx-remote-client";
import type { HeaderItem } from "@/lib/resources/schema";

interface ResourcePostPageProps {
  mdxSource: SerializeResult;
  slug: string;
  title: string;
  subtitle: string;
  seoTitle: string | null;
  seoDescription: string | null;
  featuredImage: string;
  featuredImageAlt: string;
  authorName: string;
  authorAvatarUrl: string;
  authorHref: string | null;
  publishedAt: string;
  updatedAt: string;
  toc: HeaderItem[];
}

export default function ResourcePostPage({
  mdxSource,
  slug,
  title,
  subtitle,
  seoTitle,
  seoDescription,
  featuredImage,
  featuredImageAlt,
  authorName,
  authorAvatarUrl,
  authorHref,
  publishedAt,
  updatedAt,
  toc,
}: ResourcePostPageProps) {
  const publishDate = format(new Date(publishedAt), "MMMM d, yyyy");
  const updateDate = format(new Date(updatedAt), "MMMM d, yyyy");

  if ("error" in mdxSource) {
    return <div>Error rendering post: {String(mdxSource.error)}</div>;
  }

  return (
    <>
      <NextSeo
        title={seoTitle ?? title}
        description={seoDescription ?? subtitle}
        canonical={`https://rebolthq.com/resources/${slug}`}
        openGraph={{
          url: `https://rebolthq.com/resources/${slug}`,
          title,
          description: subtitle,
          images: [{ url: featuredImage }],
        }}
      />
      <ArticleJsonLd
        useAppDir={false}
        url={`https://rebolthq.com/resources/${slug}`}
        title={title}
        description={subtitle}
        images={[featuredImage]}
        isAccessibleForFree={true}
        authorName={authorName}
        datePublished={publishedAt}
      />

      <NavbarLegacy isFixed={false} />

      <div className="bg-linear-to-br from-clay-100 to-clay-200 pt-20">
        <div className="container">
          <BlogBreadcrumbs className="-mb-4 mt-10 md:-mb-10" pageSlug={slug} pageTitle={title} />
          <BlogHero
            title={title}
            authorName={authorName}
            heroImage={featuredImage}
            heroImageAlt={featuredImageAlt}
            authorAvatarUrl={authorAvatarUrl}
            authorHref={authorHref ?? undefined}
            publishDate={publishDate}
            updateDate={updateDate}
          />
        </div>
      </div>
      <BlogLayout>
        <BlogLayout.Main>
          <div className="prose prose-lg">
            <MDXClient {...mdxSource} components={mdxComponents} />
          </div>
          <p className="my-8 text-ash-700">This article was originally published on {publishDate}</p>
        </BlogLayout.Main>
        <BlogLayout.Aside>
          <TableOfContentsMdx items={toc} />
        </BlogLayout.Aside>
      </BlogLayout>
      <Footer />
    </>
  );
}

function TableOfContentsMdx({ items, title = "Table of contents" }: { items: HeaderItem[]; title?: string }) {
  if (items.length === 0) return null;

  return (
    <nav className="bg-ash-100 p-6 rounded-xl w-full mb-8">
      <h4 className="font-semibold uppercase text-base mb-4">{title}</h4>
      <hr className="border-t border-ash-200 my-4" />
      <ol className="list-none p-0">
        {items.map(({ href, text }, index) => (
          <li key={index} className={`${index < items.length - 1 ? "mb-3" : ""}`}>
            <a
              href={href}
              className="text-rebolt-800 no-underline text-[14px] border-b border-b-rebolt-600/20 hover:border-b-rebolt-600"
            >
              {text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = getAllResourceSlugs();
  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<ResourcePostPageProps> = async ({ params }) => {
  const slug = params?.slug as string;
  const post = getResourcePostBySlug(slug);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const validationErrors = runAllValidations();
  if (validationErrors.length > 0) {
    const relevant = validationErrors.filter((e) => e.slug === slug);
    if (relevant.length > 0) {
      throw new Error(
        `Validation errors for "${slug}":\n${relevant.map((e) => `  [${e.field}] ${e.message}`).join("\n")}`
      );
    }
  }

  const mdxSource = await serializeResourceMdx(post.content);
  const toc = extractTocFromSource(post.content);
  const fm = post.frontmatter;
  const author = authors[fm.authorKey];

  return {
    props: {
      mdxSource,
      slug: fm.slug,
      title: fm.title,
      subtitle: fm.subtitle,
      seoTitle: fm.seoTitle ?? null,
      seoDescription: fm.seoDescription ?? null,
      featuredImage: fm.featuredImage,
      featuredImageAlt: fm.featuredImageAlt,
      authorName: author?.displayName ?? author?.name ?? fm.authorKey,
      authorAvatarUrl: author?.avatarUrl ?? "",
      authorHref: author?.href ?? null,
      publishedAt: fm.publishedAt.toISOString(),
      updatedAt: (fm.updatedAt ?? fm.publishedAt).toISOString(),
      toc,
    },
  };
};
